import type { AlertPayload, ImpactFeedback } from './supplierRiskAlertShared'
import {
  alertHeadline,
  formatImpactAreas,
  listSuppliersFromPayload,
  singleSourceLabel,
} from './supplierRiskAlertShared'
import { EsBadge } from './ui'

function Meta({ label, value }: { label: string; value?: string | null }) {
  if (!value || !String(value).trim()) return null
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 text-sm py-1 border-b border-es-hairline/60">
      <dt className="text-es-muted">{label}</dt>
      <dd className="text-es-navy font-medium">{value}</dd>
    </div>
  )
}

function BulletBlock({ title, text }: { title: string; text?: string }) {
  if (!text?.trim()) return null
  const lines = text
    .split(/\n+/)
    .map((l) => l.replace(/^[-•*]\s*/, '').trim())
    .filter(Boolean)
  return (
    <section className="space-y-2">
      <h3 className="font-esDisplay text-base text-es-navy">{title}</h3>
      <ul className="list-disc pl-5 text-sm text-es-body space-y-1">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
    </section>
  )
}

/** Read-only Word/Stepan-style alert body */
export function SupplierRiskAlertDocument({
  payload,
  logoUrl,
  status,
}: {
  payload: AlertPayload
  logoUrl?: string | null
  status?: string
}) {
  const severity = String(payload.severity || 'HIGH').toUpperCase()
  const overview = payload.eventOverview || payload.overview || ''
  const rows = (payload.priceIncreases || []).filter(
    (r) => r.productCategory || r.tradeNames || r.increase,
  )
  const suppliers = listSuppliersFromPayload(payload)
  const multi = suppliers.length > 1

  return (
    <article className="rounded-lg border border-es-hairline bg-es-white p-5 sm:p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {logoUrl && (
            <img src={logoUrl} alt="" className="h-10 max-w-[140px] object-contain" />
          )}
          <div>
            <h2 className="font-esDisplay text-xl text-es-navy">Supplier risk alert</h2>
            {status && (
              <p className="mt-1">
                <EsBadge tone="quiet">{status.replace(/_/g, ' ')}</EsBadge>
              </p>
            )}
          </div>
        </div>
        <span
          className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-white ${
            severity === 'HIGH' ? 'bg-red-800' : severity === 'MEDIUM' ? 'bg-amber-700' : 'bg-emerald-800'
          }`}
        >
          Risk Impact {severity}
        </span>
      </div>

      <p className="text-base font-semibold text-es-navy">{alertHeadline(payload)}</p>

      {payload.immediateAction?.trim() && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
          <p className="font-semibold text-amber-950">Immediate action required</p>
          <p className="mt-1 text-amber-950 whitespace-pre-wrap">{payload.immediateAction}</p>
        </div>
      )}

      {multi ? (
        <section className="space-y-2">
          <h3 className="font-esDisplay text-base text-es-navy">Suppliers affected</h3>
          <ul className="space-y-2">
            {suppliers.map((s) => (
              <li
                key={`${s.supplierName}-${s.uniqueId || ''}`}
                className="rounded-md border border-es-hairline px-3 py-2 text-sm"
              >
                <p className="font-medium text-es-navy">{s.supplierName}</p>
                <p className="text-xs text-es-muted mt-0.5">
                  {[s.uniqueId, s.category, s.plant, s.brand, singleSourceLabel(s.singleSource) !== '—' ? `SS: ${singleSourceLabel(s.singleSource)}` : '']
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </p>
              </li>
            ))}
          </ul>
          <dl className="pt-1">
            <Meta label="Location" value={payload.location} />
            <Meta label="Region" value={payload.region} />
          </dl>
        </section>
      ) : (
        <dl>
          <Meta label="Unique ID" value={suppliers[0]?.uniqueId || payload.uniqueId} />
          <Meta label="Supplier" value={suppliers[0]?.supplierName || payload.supplierName} />
          <Meta label="Location" value={suppliers[0]?.location || payload.location} />
          <Meta label="Region" value={suppliers[0]?.region || payload.region} />
          <Meta label="Category" value={suppliers[0]?.category || payload.category} />
          <Meta label="Sub-Category" value={suppliers[0]?.subCategory || payload.subCategory} />
          <Meta label="Plant" value={suppliers[0]?.plant || payload.plant} />
          <Meta label="Brand" value={suppliers[0]?.brand || payload.brand} />
          <Meta
            label="Single Source"
            value={singleSourceLabel(suppliers[0]?.singleSource ?? payload.singleSource)}
          />
        </dl>
      )}

      {overview.trim() && (
        <section className="space-y-2">
          <h3 className="font-esDisplay text-base text-es-navy">Event Overview</h3>
          <p className="text-sm text-es-body whitespace-pre-wrap">{overview}</p>
        </section>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-es-hairline">
            <thead className="bg-es-haze">
              <tr>
                <th className="text-left p-2 border-b border-es-hairline">Product category</th>
                <th className="text-left p-2 border-b border-es-hairline">Trade name(s)</th>
                <th className="text-left p-2 border-b border-es-hairline">Increase</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="p-2 border-b border-es-hairline">{r.productCategory}</td>
                  <td className="p-2 border-b border-es-hairline">{r.tradeNames}</td>
                  <td className="p-2 border-b border-es-hairline">{r.increase}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BulletBlock title="Why it matters" text={payload.whyItMatters} />
      <BulletBlock title="Next best action" text={payload.nextBestAction} />

      {payload.keyTakeaway?.trim() && (
        <p className="text-sm text-es-navy">
          <strong>KEY TAKEAWAY</strong> {payload.keyTakeaway}
        </p>
      )}
    </article>
  )
}

export function ImpactFeedbackPanel({
  feedback,
  title = 'Your response',
}: {
  feedback: ImpactFeedback
  title?: string
}) {
  return (
    <section className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-5 space-y-3">
      <h3 className="font-esDisplay text-lg text-es-navy">{title}</h3>
      <dl className="space-y-2 text-sm">
        <div>
          <dt className="text-es-muted">Area(s) of Potential Impact</dt>
          <dd className="font-medium text-es-navy">{formatImpactAreas(feedback)}</dd>
        </div>
        <div>
          <dt className="text-es-muted">Supply / service status</dt>
          <dd className="font-medium text-es-navy">{feedback.supply_status}</dd>
        </div>
        <div>
          <dt className="text-es-muted">Expected impact duration</dt>
          <dd className="font-medium text-es-navy">{feedback.impact_duration}</dd>
        </div>
        <div>
          <dt className="text-es-muted">Impact details</dt>
          <dd className="font-medium text-es-navy whitespace-pre-wrap">{feedback.impact_details}</dd>
        </div>
        {(feedback.submitted_by_email || feedback.submitted_at) && (
          <p className="text-xs text-es-muted pt-1">
            {feedback.submitted_by_email ? `Submitted by ${feedback.submitted_by_email}` : 'Submitted'}
            {feedback.submitted_at
              ? ` · ${new Date(feedback.submitted_at).toLocaleString()}`
              : ''}
          </p>
        )}
      </dl>
    </section>
  )
}
